// The world on screen: the stage, the town, one Character per body in the PublicView, the Town Crier on the bandstand,
// the FX, and picking (which townie did the Inspector click). It reads views; it never decides gameplay.

import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Stage, type Tier } from "./core";
import { Town, toonify, type TownManifest } from "./town";
import { Character, setClips, type AnimName } from "./chars";
import { Fx, Sheet } from "./fx";
import { glb, optional, texture } from "./assets";
import { RESIDENTS, INSPECTOR_COSTUME } from "../../../shared/residents";
import { CRIER } from "../../../shared/map";
import type { PublicView, PublicBody, Cosmetics } from "../../../shared/sim";
import { COSMETICS, type CosKind } from "../meta/catalog";
import * as T from "../../../shared/tuning";

export const SNEAK_COLOURS: Record<string, string> = {
  col_plum: "#6B4AA0", col_mint: "#5FC9A0", col_tangerine: "#F2894A", col_sky: "#5AA6E6", col_cherry: "#D8475C",
  col_lemon: "#E8C64A", col_night: "#2F3A6B", col_rainbow: "#E86AA8",
};
/** cosmetic index on the wire (per kind, catalogue order) ↔ catalogue id */
export const cosIds = (kind: CosKind) => COSMETICS.filter((c) => c.kind === kind).map((c) => c.id);
export const cosIndex = (id: string) => { const c = COSMETICS.find((x) => x.id === id); return c ? cosIds(c.kind).indexOf(id) : 0; };

interface Shown { ch: Character; costume: number; revealed: boolean; lastX: number; lastZ: number; speed: number; anim: AnimName; seenAt: number; }

export class World {
  stage: Stage;
  town: Town;
  fx = new Fx();
  private tpl = new Map<string, GLTF>();
  private shown = new Map<number, Shown>();
  private crier: Character | null = null;
  private you: THREE.Object3D | null = null;
  meId = 0;
  ready: Promise<void>;
  private ray = new THREE.Raycaster();

  constructor(canvas: HTMLCanvasElement, tier: Tier, private man: TownManifest & { chars?: Record<string, { file: string; hairAllowed?: string[]; defaultHair?: string }>; cosmetics?: Record<string, { file: string }>; fx?: Record<string, { file: string; cols: number; rows: number; frames?: number }> }) {
    this.stage = new Stage(canvas, tier);
    this.town = new Town(man, tier);
    this.stage.scene.add(this.town.group, this.fx.group);
    this.ready = this.load();
  }

  private charFile(id: string) { return "./assets/" + (this.man.chars?.[id]?.file ?? `chars/${id}.glb`); }
  private cosFile(id: string) { return "./assets/" + (this.man.cosmetics?.[id]?.file ?? `cos/${id}.glb`); }

  private async load() {
    const clips = await glb("./assets/anim/clips.glb"); setClips(clips);
    const ids = [...RESIDENTS, "inspector", "sneak", "crier"];
    await Promise.all(ids.map(async (id) => { const g = await optional(glb(this.charFile(id))); if (g) this.tpl.set(id, g); }));
    await this.town.ready;
    for (const [name, info] of Object.entries(this.man.fx ?? {})) {
      const tex = await optional(texture(`./assets/ui/${info.file}`));
      if (tex) this.fx.add(name, new Sheet(tex, info));
    }
    const ct = this.template("crier");
    if (ct) { this.crier = new Character(ct, { tier: this.stage.tier, skin: 1 }); this.crier.root.position.set(CRIER.x, CRIER.y, CRIER.z); this.crier.root.rotation.y = CRIER.rot; this.stage.scene.add(this.crier.root); }
  }

  private template(id: string): GLTF | null { return this.tpl.get(id) ?? this.tpl.get("grandma") ?? null; }
  private costumeId(c: number) { return c === INSPECTOR_COSTUME ? "inspector" : RESIDENTS[c] ?? "grandma"; }
  /** skin and hair roll per body from its id (stable for the body's life), never a type cue (ART_BIBLE §3.2) */
  private looks(id: string, bodyId: number) {
    const hairs = this.man.chars?.[id]?.hairAllowed ?? [];
    const h = (bodyId * 2654435761) >>> 0;
    return { skin: h % 4, hair: hairs.length ? hairs[(h >>> 8) % hairs.length] : undefined };
  }

  private spawn(b: PublicBody, reveals: { id: number; cos: Cosmetics }[]): Shown | null {
    const revealed = b.revealed;
    const id = revealed ? "sneak" : this.costumeId(b.costume);
    const t = this.template(id); if (!t) return null;
    const rv = revealed ? reveals.find((r) => r.id === b.id)?.cos : undefined;
    const body = revealed ? SNEAK_COLOURS[cosIds("color")[rv?.colour ?? 0] ?? "col_plum"] ?? "#6B4AA0" : undefined;
    const ch = new Character(t, { tier: this.stage.tier, ...this.looks(id, b.id), body });
    if (revealed) this.attachCos(ch, cosIds("mask")[rv?.mask ?? 0] ?? "mask_classic");
    if (id === "inspector") this.attachCos(ch, cosIds("hat")[this.inspHat] ?? "hat_deerstalker");
    ch.root.position.set(b.x, 0, b.z); ch.root.rotation.y = b.h;
    this.stage.scene.add(ch.root);
    return { ch, costume: b.costume, revealed, lastX: b.x, lastZ: b.z, speed: 0, anim: "idle", seenAt: 0 };
  }

  /** masks and hats: GLB origin = the head joint, no offset (manifest conventions.cosmetics) */
  private async attachCos(ch: Character, id: string) {
    const g = await optional(glb(this.cosFile(id)));
    if (!g) return;
    const o = g.scene.clone(true);
    toonify(o, this.stage.tier, true);
    ch.attach(o, "head");
  }
  inspHat = 0;

  /** one frame: sync bodies from the view, step characters and FX, place the camera */
  sync(pub: PublicView, dt: number, now: number) {
    this.inspHat = pub.inspectorCos?.hat ?? 0;
    const live = new Set<number>();
    for (const b of pub.bodies) {
      live.add(b.id);
      let s = this.shown.get(b.id);
      if (s && s.revealed !== b.revealed) { s.ch.dispose(); this.shown.delete(b.id); s = undefined; }
      if (!s) { const n = this.spawn(b, pub.reveals); if (!n) continue; s = n; this.shown.set(b.id, s); }
      const ch = s.ch;
      // speed from the rendered motion (drives walk/run playback so feet don't skate)
      const d = Math.hypot(b.x - s.lastX, b.z - s.lastZ);
      s.speed = s.speed * 0.8 + (dt > 0 ? d / dt : 0) * 0.2; s.lastX = b.x; s.lastZ = b.z;
      ch.speed = s.speed;
      if (b.anim === "ragdoll") {
        if (!ch.ragdoll) {
          const away = new THREE.Vector3(b.x - this.stage.focus.x, 0, b.z - this.stage.focus.z).normalize();
          ch.tumble(b.revealed ? away.multiplyScalar(4.5).setY(7.5) : away.multiplyScalar(1.4).setY(2.6), b.revealed ? 5 : 2.5);
        }
      } else {
        if (ch.ragdoll) ch.endTumble();
        ch.root.position.set(b.x, 0, b.z); ch.root.rotation.y = b.h;
        if (b.anim !== s.anim || ch.curName !== b.anim) { ch.play(b.anim as AnimName, 0.18, b.age); s.anim = b.anim as AnimName; }
      }
      const far = this.stage.tier !== "high" && Math.hypot(b.x - this.stage.focus.x, b.z - this.stage.focus.z) > 12;
      ch.update(dt, far);
    }
    for (const [id, s] of this.shown) if (!live.has(id)) {
      if (s.revealed && s.ch.ragdoll) this.fx.puff(s.ch.ragdoll.center.x, s.ch.ragdoll.center.z, 10, true);
      s.ch.dispose(); this.shown.delete(id);
    }
    if (this.town.roof) {
      const near = Math.hypot(this.stage.focus.x - CRIER.x, this.stage.focus.z - CRIER.z) < 10;
      this.town.roof.traverse((m: any) => { if (m.isMesh && m.material) { const mats = [].concat(m.material) as any[]; for (const x of mats) { x.transparent = true; x.opacity += ((near ? 0.25 : 1) - x.opacity) * Math.min(1, dt * 4); x.depthWrite = x.opacity > 0.9; } } });
    }
    if (this.crier) {
      const want: AnimName = pub.call && pub.call.age < T.CALL_HOLD ? "wave" : "idle";
      if (this.crier.curName !== want) this.crier.play(want);
      this.crier.update(dt);
    }
    this.town.setStalls(pub.stalls);
    this.town.setBus(pub.bus.state, pub.bus.age);
    this.town.setFloat(pub.float, pub.golden);
    this.town.update(dt);
    this.fx.update(dt);
    void now;
  }

  bodyPos(id: number): THREE.Vector3 | null { const s = this.shown.get(id); if (!s) return null; return s.ch.ragdoll ? s.ch.ragdoll.center.clone() : s.ch.root.position.clone(); }
  headPos(id: number): THREE.Vector3 | null { const p = this.bodyPos(id); if (!p) return null; const s = this.shown.get(id)!; return p.setY((s.ch.ragdoll ? 0.5 : 1.45) * s.ch.scale); }

  /** the townie under a screen point: nearest body whose chest projects within a forgiving radius */
  pick(sx: number, sy: number, excludeId = 0): { id: number; aim: { x: number; y: number; z: number } } | null {
    const cam = this.stage.camera, el = this.stage.renderer.domElement;
    const w = el.clientWidth, h = el.clientHeight;
    let best: { id: number; d: number; p: THREE.Vector3 } | null = null;
    for (const [id, s] of this.shown) {
      if (id === excludeId || s.revealed || s.ch.ragdoll) continue;
      const chest = s.ch.root.position.clone().setY(0.8 * s.ch.scale);
      const v = chest.clone().project(cam); if (v.z > 1) continue;
      const px = (v.x * 0.5 + 0.5) * w, py = (-v.y * 0.5 + 0.5) * h;
      // radius: half a townie's on-screen height, at least 22 px (fat fingers)
      const head = s.ch.root.position.clone().setY(1.35 * s.ch.scale).project(cam);
      const r = Math.max(22, Math.abs((-head.y * 0.5 + 0.5) * h - py) * 1.2);
      const d = Math.hypot(px - sx, py - sy);
      if (d <= r && (!best || d < best.d)) best = { id, d, p: chest };
    }
    return best ? { id: best.id, aim: { x: best.p.x, y: best.p.y, z: best.p.z } } : null;
  }

  /** the YOU marker floats over my body (painted arrow icon billboard) */
  setYou(obj: THREE.Object3D | null) { if (this.you) this.you.removeFromParent(); this.you = obj; if (obj) this.stage.scene.add(obj); }
  placeYou(id: number, visible: boolean) { if (!this.you) return; const p = this.headPos(id); this.you.visible = visible && !!p; if (p) this.you.position.copy(p).setY(p.y + 0.55); }

  render(dt: number) { this.stage.frame(dt); this.stage.render(); }
}
